/**
 * The PRD-6.1 invite row (SE-8313): a people search, a `Can edit` / `Can view`
 * Select and an Invite button, with the picked person as a pill, the matching
 * suggestions beneath the search, and an inline error when an Invite press
 * cannot resolve to one person who lacks access.
 * SE-8359 (PV17): "Everyone at {organization}" is offered as a candidate too,
 * pinned above the people whenever the owner may still invite it; picked, it
 * takes the pill the picked person would.
 * @module features/scouting/components/molecules/ShareInviteRow/ShareInviteRowView
 */

import { ChevronDownIcon, UsersIcon } from "lucide-react-native";
import { memo } from "react";

import ShareCandidateRow from "./ShareCandidateRow";

import SharePill from "@/components/molecules/SharePill";

import { Avatar } from "@/components/atoms/Avatar";
import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Icon } from "@/components/atoms/Icon";
import { Input } from "@/components/atoms/Input";
import { Pressable } from "@/components/atoms/Pressable";
import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectScrollView,
  SelectTrigger,
} from "@/components/atoms/Select";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import { TEST_ID_WATCHLIST_SHARE } from "e2e/testId";

/** One invite suggestion. */
export interface ShareInviteCandidate {
  /** User id. */
  readonly userId: string;
  /** Display name. */
  readonly name: string;
}

/** The one group the owner may invite (SE-8359). */
export interface ShareInviteGroupCandidate {
  /** `ShareGroupKey` value (test identity). */
  readonly key: string;
  /** "Everyone at {organization}". */
  readonly label: string;
  /** Members the grant reaches. */
  readonly memberCount: number;
}

/** One selectable role. */
export interface ShareInviteRoleOption {
  /** Select value (a grantable role). */
  readonly value: string;
  /** Visible label. */
  readonly label: string;
}

/** Props for {@link ShareInviteRowView}. */
export interface ShareInviteRowViewProps {
  /** Current search text. */
  readonly searchQuery: string;
  /** Suggestions for the current search (empty without a query). */
  readonly candidates: readonly ShareInviteCandidate[];
  /** The picked suggestion, if any. */
  readonly selectedCandidate: ShareInviteCandidate | null;
  /** The group candidate, pinned first among the suggestions; null = not offered. */
  readonly groupCandidate?: ShareInviteGroupCandidate | null;
  /** The picked group, if any. */
  readonly selectedGroup?: ShareInviteGroupCandidate | null;
  /** Picks the group candidate. */
  readonly onSelectGroup?: () => void;
  /** Role the next invite grants (Select value). */
  readonly inviteRole: string;
  /** Label of the selected role option. */
  readonly inviteRoleLabel: string;
  /** Selectable roles. */
  readonly roleOptions: readonly ShareInviteRoleOption[];
  /** Inline error, or null. */
  readonly inviteError: string | null;
  /** Whether an invite is in flight. */
  readonly isInviting: boolean;
  /** Search change handler. */
  readonly onSearchChange: (next: string) => void;
  /** Suggestion pick handler (keyed by user id). */
  readonly onSelectCandidate: (userId: string) => void;
  /** Clears the picked suggestion (keyed by user id, from the pill). */
  readonly onClearCandidate: (userId: string) => void;
  /** Role Select change handler. */
  readonly onInviteRoleChange: (value: string) => void;
  /** Invite press handler. */
  readonly onInvite: () => void;
}

/**
 * Renders the invite row.
 * @param root0 - View props
 * @param root0.searchQuery - Current search text
 * @param root0.candidates - Suggestions for the current search
 * @param root0.selectedCandidate - The picked suggestion
 * @param root0.groupCandidate - The group candidate, or null
 * @param root0.selectedGroup - The picked group, or null
 * @param root0.onSelectGroup - Picks the group candidate
 * @param root0.inviteRole - Role the next invite grants
 * @param root0.inviteRoleLabel - Label of the selected role option
 * @param root0.roleOptions - Selectable roles
 * @param root0.inviteError - Inline error
 * @param root0.isInviting - Invite in flight
 * @param root0.onSearchChange - Search change handler
 * @param root0.onSelectCandidate - Suggestion pick handler
 * @param root0.onClearCandidate - Clears the picked suggestion
 * @param root0.onInviteRoleChange - Role change handler
 * @param root0.onInvite - Invite press handler
 * @returns The rendered invite row.
 */
const ShareInviteRowView = ({
  searchQuery,
  candidates,
  selectedCandidate,
  groupCandidate = null,
  selectedGroup = null,
  onSelectGroup,
  inviteRole,
  inviteRoleLabel,
  roleOptions,
  inviteError,
  isInviting,
  onSearchChange,
  onSelectCandidate,
  onClearCandidate,
  onInviteRoleChange,
  onInvite,
}: ShareInviteRowViewProps) => (
  <Stack className="gap-2">
    <Stack direction="horizontal" className="items-center gap-2">
      <Input
        variant="outline"
        size="md"
        className="flex-1 rounded-sm"
        field={{
          testID: TEST_ID_WATCHLIST_SHARE.INVITE_SEARCH,
          value: searchQuery,
          // Figma 970:19119: a plain field — people or the org group.
          placeholder: "Add people or groups…",
          autoCorrect: false,
          spellCheck: false,
          editable: !isInviting,
          onChangeText: onSearchChange,
          className: "text-body text-content-primary",
        }}
      />
      <Select
        selectedValue={inviteRole}
        onValueChange={onInviteRoleChange}
        isDisabled={isInviting}
      >
        <SelectTrigger
          testID={TEST_ID_WATCHLIST_SHARE.INVITE_ROLE}
          accessibilityLabel="Role for the invited person"
          variant="outline"
          size="sm"
          // Figma 970:19119: the role select is 130 wide (w-32 = 128, ties
          // round down); the field takes the rest.
          className="h-10 w-32 rounded-sm border-outline-default px-3"
        >
          <SelectInput
            value={inviteRoleLabel}
            placeholder="Role"
            className="text-body"
          />
          <SelectIcon as={ChevronDownIcon} className="text-content-muted" />
        </SelectTrigger>
        {/* useRNModal: the row is hosted inside MobileSheetChrome's RN Modal on
            native, and a Select sheet rendered through the root overlay
            provider sits BELOW that Modal (Galaxy S24: the role sheet never
            appeared). A nested RN Modal is the only host that stacks above it;
            the web branch renders a native <select> and ignores the prop. */}
        <SelectPortal useRNModal>
          <SelectBackdrop />
          <SelectContent>
            <SelectScrollView>
              {roleOptions.map(option => (
                <SelectItem
                  key={option.value}
                  label={option.label}
                  value={option.value}
                />
              ))}
            </SelectScrollView>
          </SelectContent>
        </SelectPortal>
      </Select>
      <Button
        testID={TEST_ID_WATCHLIST_SHARE.INVITE_BUTTON}
        label="Invite"
        tone="accent"
        size="sm"
        onPress={onInvite}
        isLoading={isInviting}
        isDisabled={isInviting}
        className="h-10 rounded-sm"
      />
    </Stack>
    {selectedCandidate ? (
      <Stack direction="horizontal" className="flex-wrap gap-2">
        <SharePill
          userId={selectedCandidate.userId}
          name={selectedCandidate.name}
          onRemove={onClearCandidate}
          variant="solid"
          testID={TEST_ID_WATCHLIST_SHARE.INVITE_SELECTED}
        />
      </Stack>
    ) : null}
    {selectedGroup ? (
      <Stack direction="horizontal" className="flex-wrap gap-2">
        <SharePill
          userId={selectedGroup.key}
          name={selectedGroup.label}
          onRemove={onClearCandidate}
          variant="solid"
          testID={TEST_ID_WATCHLIST_SHARE.GROUP_SELECTED}
        />
      </Stack>
    ) : null}
    {!selectedCandidate &&
    !selectedGroup &&
    (candidates.length > 0 || groupCandidate) ? (
      <Stack
        testID={TEST_ID_WATCHLIST_SHARE.INVITE_SUGGESTIONS}
        className="rounded-sm border border-outline-default"
      >
        {groupCandidate ? (
          <Pressable
            testID={TEST_ID_WATCHLIST_SHARE.GROUP_CANDIDATE}
            onPress={onSelectGroup}
            accessibilityRole="button"
            accessibilityLabel={`Invite ${groupCandidate.label}`}
            className="web:hover:bg-surface-raised flex-row items-center gap-3 px-3 py-2"
          >
            <Avatar size="sm" className="bg-surface-muted">
              <Icon
                as={UsersIcon}
                size="sm"
                className="text-content-secondary"
              />
            </Avatar>
            <Stack className="min-w-0 flex-1">
              <Text
                variant="body"
                className="text-content-primary"
                numberOfLines={1}
              >
                {groupCandidate.label}
              </Text>
              <Text variant="caption" className="text-content-muted">
                {groupCandidate.memberCount}{" "}
                {groupCandidate.memberCount === 1 ? "person" : "people"}
              </Text>
            </Stack>
          </Pressable>
        ) : null}
        {candidates.map(candidate => (
          <ShareCandidateRow
            key={candidate.userId}
            userId={candidate.userId}
            name={candidate.name}
            onSelect={onSelectCandidate}
          />
        ))}
      </Stack>
    ) : null}
    {inviteError ? (
      <Box testID={TEST_ID_WATCHLIST_SHARE.INVITE_ERROR}>
        <Text variant="caption" className="text-status-error">
          {inviteError}
        </Text>
      </Box>
    ) : null}
  </Stack>
);

ShareInviteRowView.displayName = "ShareInviteRowView";

export default memo(ShareInviteRowView);
